import React, { useState, useRef, useEffect } from "react";
import { useCryptoContext } from "../contexts/CryptoContext";
import { useI18n } from "../contexts/I18nContext";

/**
 * SidebarUnlock
 * Lightweight unlock modal rendered inside the sidebar.
 * User enters their Benutzer-Schlüssel, sidebar performs all crypto.
 *
 * @param {Function} onSuccess - Called after successful unlock
 * @param {Function} onCancel - Called when user cancels
 */
export default function SidebarUnlock({ onSuccess, onCancel }) {
  const { t } = useI18n();
  const { unlock } = useCryptoContext();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Auto-focus password input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      setError(t("Bitte geben Sie ein Passwort ein."));
      return;
    }

    setSubmitting(true);
    setError(null);

    const result = await unlock(password);

    if (result.success) {
      setPassword("");
      onSuccess();
    } else {
      setError(result.error);
    }

    setSubmitting(false);
  };

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.title}>{t("Passwort-Manager entsperren")}</h3>
        <p style={styles.description}>
          {t("Geben Sie Ihren Benutzer-Schlüssel ein, um die Passwortdatenbank zu entsperren.")}
        </p>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>{t("Benutzer-Schlüssel")}</label>
          <input
            ref={inputRef}
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            placeholder={t("Passwort eingeben...")}
            style={styles.input}
          />

          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              disabled={submitting}
            />
            <span style={{ marginLeft: "6px" }}>{t("Passwort anzeigen")}</span>
          </label>

          <div style={styles.actions}>
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              style={styles.cancelButton}
            >
              {t("Abbrechen")}
            </button>
            <button
              type="submit"
              disabled={submitting || !password}
              style={{
                ...styles.submitButton,
                ...(submitting || !password ? styles.buttonDisabled : {}),
              }}
            >
              {submitting ? t("Wird entsperrt...") : t("Entsperren")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "0 12px",
  },
  modal: {
    background: "#fff",
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
    padding: "20px 16px",
    width: "calc(100% - 32px)",
    maxWidth: "340px",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
  },
  description: {
    margin: "0 0 16px 0",
    fontSize: "13px",
    color: "#666",
    lineHeight: "1.5",
  },
  error: {
    padding: "10px 14px",
    marginBottom: "12px",
    borderRadius: "6px",
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    color: "#721c24",
    fontSize: "13px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    boxSizing: "border-box",
    marginBottom: "10px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    color: "#555",
    marginBottom: "16px",
    cursor: "pointer",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  cancelButton: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #ccc",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#333",
    cursor: "pointer",
    width: "100%",
  },
  submitButton: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#0078d7",
    color: "#fff",
    cursor: "pointer",
    width: "100%",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};
